// packages/observability/src/metrics.ts
//
// Server-side error-rate metric. Emits an `http.server.errors` counter so
// SigNoz can alert on 5xx rate by route/status/workspace — the operational
// signal PostHog (the exception home) can't provide. On Vercel the metric
// reader can't rely on its interval (the function freezes after the response),
// so the route helpers flush it via next/server `after()`, mirroring the logs
// path. Gateways hand the same reader to NodeSDK (node.ts).

import { metrics } from "@opentelemetry/api";
import type { Attributes, Counter, Gauge, Histogram } from "@opentelemetry/api";
import {
  PeriodicExportingMetricReader,
  type MetricReader,
} from "@opentelemetry/sdk-metrics";

import { makeMetricExporter } from "./exporters";

export const ATTR_HTTP_SERVER_ERRORS = "http.server.errors";
export const ATTR_HTTP_SERVER_REQUESTS = "http.server.requests";
export const ATTR_HTTP_SERVER_DURATION = "http.server.duration";
export const ATTR_VOIP_INBOX_READY_COUNT = "voip.inbox.ready_count";
export const ATTR_VOIP_INBOX_OLDEST_AGE = "voip.inbox.oldest_age_seconds";
export const ATTR_VOIP_EFFECTS_READY_COUNT = "voip.effects.ready_count";
export const ATTR_VOIP_EFFECTS_OLDEST_AGE = "voip.effects.oldest_age_seconds";
export const ATTR_VOIP_EFFECTS_FAILED_COUNT = "voip.effects.failed_count";
export const ATTR_VOIP_DLQ_UNRESOLVED_COUNT = "voip.dlq.unresolved_count";
export const ATTR_VOIP_WEBHOOK_ACK_DURATION = "voip.webhook.ack.duration_ms";
export const ATTR_VOIP_WEBHOOK_ACK_REQUESTS = "voip.webhook.ack.requests";
export const ATTR_VOIP_INBOX_CLAIM_DURATION = "voip.inbox.claim.duration_ms";
export const ATTR_VOIP_REDUCER_APPLY_DURATION =
  "voip.reducer.apply.duration_ms";
export const ATTR_VOIP_REDUCER_VERSION_CONFLICTS =
  "voip.reducer.version_conflicts";
export const ATTR_VOIP_LEASE_EXPIRED = "voip.lease_expired";
export const ATTR_VOIP_LEASE_FENCE_REJECTED = "voip.lease_fence_rejected";
export const ATTR_VOIP_EFFECTS_RETRIES = "voip.effects.retries";
export const ATTR_VOIP_EFFECTS_TERMINAL_FAILURES =
  "voip.effects.terminal_failures";
export const ATTR_VOIP_COMMAND_RECONCILIATION = "voip.commands.reconciliation";
export const ATTR_VOIP_DLQ_INSERTED = "voip.dlq.inserted";

let serverReader: MetricReader | undefined;

/**
 * Build the reader the web app passes to @vercel/otel's `metricReaders` (and
 * gateways to NodeSDK's, in node.ts), and keep a reference so
 * `flushServerMetrics()` can drain it from `after()`.
 * Memoised so repeated `registerNextObservability` calls reuse one reader.
 */
export function getServerMetricReader(): MetricReader {
  serverReader ??= new PeriodicExportingMetricReader({
    exporter: makeMetricExporter(),
    // Interval is a backstop only — the real drain is the after()-driven flush.
    exportIntervalMillis: 60_000,
  });
  return serverReader;
}

let counter: Counter | undefined;
let requestCounter: Counter | undefined;
let durationHistogram: Histogram | undefined;
let voipReadyGauge: Gauge | undefined;
let voipOldestAgeGauge: Gauge | undefined;
let voipEffectsReadyGauge: Gauge | undefined;
let voipEffectsOldestAgeGauge: Gauge | undefined;
let voipEffectsFailedGauge: Gauge | undefined;
let voipDlqUnresolvedGauge: Gauge | undefined;
let voipMessagesPendingGauge: Gauge | undefined;
let voipMessagesAmbiguousGauge: Gauge | undefined;
let voipMessagesOldestAgeGauge: Gauge | undefined;
let voipWebhookAckDuration: Histogram | undefined;
let voipWebhookAckRequests: Counter | undefined;
let voipInboxClaimDuration: Histogram | undefined;
let voipReducerApplyDuration: Histogram | undefined;
let voipVersionConflicts: Counter | undefined;
let voipLeaseExpired: Counter | undefined;
let voipLeaseFenceRejected: Counter | undefined;
let voipEffectRetries: Counter | undefined;
let voipEffectTerminalFailures: Counter | undefined;
let voipCommandReconciliation: Counter | undefined;
let voipDlqInserted: Counter | undefined;
function errorCounter(): Counter {
  counter ??= metrics
    .getMeter("@atriam/observability", "1.0.0")
    .createCounter(ATTR_HTTP_SERVER_ERRORS, {
      description: "Count of server-side 5xx responses",
    });
  return counter;
}

export interface ServerErrorAttrs {
  route: string;
  method: string;
  status: number;
  workspace_id?: string;
}

export function recordServerError(attrs: ServerErrorAttrs): void {
  const labels: Attributes = {
    route: normalizeHttpRoute(attrs.route),
    method: attrs.method,
    status: attrs.status,
  };
  errorCounter().add(1, labels);
}

export interface ServerResponseAttrs {
  route: string;
  method: string;
  status: number;
  duration_ms: number;
}

const UUID_ROUTE_SEGMENT =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const NUMERIC_ROUTE_SEGMENT = /^\d{4,}$/;

export function normalizeHttpRoute(path: string): string {
  const pathname = path.split(/[?#]/, 1)[0] || "/";
  return pathname
    .split("/")
    .map((segment) =>
      UUID_ROUTE_SEGMENT.test(segment) || NUMERIC_ROUTE_SEGMENT.test(segment)
        ? ":id"
        : segment,
    )
    .join("/");
}

function requests(): Counter {
  requestCounter ??= metrics
    .getMeter("@atriam/observability", "1.0.0")
    .createCounter(ATTR_HTTP_SERVER_REQUESTS, {
      description: "Count of server-side HTTP responses",
    });
  return requestCounter;
}

function durations(): Histogram {
  durationHistogram ??= metrics
    .getMeter("@atriam/observability", "1.0.0")
    .createHistogram(ATTR_HTTP_SERVER_DURATION, {
      description: "Server-side HTTP response duration",
      unit: "ms",
      // Without explicit boundaries OTel's default set tops out at 10s, so every
      // internal tick (agent-step and other batch workers can exceed 100s
      // under a 300s maxDuration) lands in the +Inf overflow bucket and any p95
      // SigNoz renders above 10s is interpolation over an unbounded bucket.
      // These extend the default ladder through the 300s function ceiling.
      advice: {
        explicitBucketBoundaries: [
          5, 25, 50, 100, 250, 500, 1000, 2500, 5000, 10_000, 30_000, 60_000,
          120_000, 300_000,
        ],
      },
    });
  return durationHistogram;
}

export function recordServerResponse(attrs: ServerResponseAttrs): void {
  const labels: Attributes = {
    route: normalizeHttpRoute(attrs.route),
    method: attrs.method,
    status_class: `${Math.floor(attrs.status / 100)}xx`,
  };
  requests().add(1, labels);
  durations().record(Math.max(0, attrs.duration_ms), labels);
}

export interface VoipQueueHealth {
  readyCount: number;
  oldestAgeSeconds: number;
}

function readyCountGauge(): Gauge {
  voipReadyGauge ??= metrics
    .getMeter("@atriam/observability", "1.0.0")
    .createGauge(ATTR_VOIP_INBOX_READY_COUNT, {
      description: "Number of webhook inbox events ready to be claimed",
      unit: "{event}",
    });
  return voipReadyGauge;
}

function oldestAgeGauge(): Gauge {
  voipOldestAgeGauge ??= metrics
    .getMeter("@atriam/observability", "1.0.0")
    .createGauge(ATTR_VOIP_INBOX_OLDEST_AGE, {
      description: "Age of the oldest webhook inbox event ready to be claimed",
      unit: "s",
    });
  return voipOldestAgeGauge;
}

export function recordVoipQueueHealth(health: VoipQueueHealth): void {
  readyCountGauge().record(Math.max(0, health.readyCount));
  oldestAgeGauge().record(Math.max(0, health.oldestAgeSeconds));
}

export interface VoipDurableQueueHealth {
  inbox: VoipQueueHealth;
  effects: VoipQueueHealth & { failedCount: number };
  dlq: { unresolvedCount: number };
}

function voipGauge(
  current: Gauge | undefined,
  name: string,
  description: string,
  unit: string,
): Gauge {
  return (
    current ??
    metrics
      .getMeter("@atriam/observability", "1.0.0")
      .createGauge(name, { description, unit })
  );
}

export function recordVoipDurableQueueHealth(
  health: VoipDurableQueueHealth,
): void {
  recordVoipQueueHealth(health.inbox);
  voipEffectsReadyGauge = voipGauge(
    voipEffectsReadyGauge,
    ATTR_VOIP_EFFECTS_READY_COUNT,
    "Provider effects ready to claim",
    "{effect}",
  );
  voipEffectsOldestAgeGauge = voipGauge(
    voipEffectsOldestAgeGauge,
    ATTR_VOIP_EFFECTS_OLDEST_AGE,
    "Age of the oldest provider effect ready to claim",
    "s",
  );
  voipEffectsFailedGauge = voipGauge(
    voipEffectsFailedGauge,
    ATTR_VOIP_EFFECTS_FAILED_COUNT,
    "Terminally failed provider effects",
    "{effect}",
  );
  voipDlqUnresolvedGauge = voipGauge(
    voipDlqUnresolvedGauge,
    ATTR_VOIP_DLQ_UNRESOLVED_COUNT,
    "Unresolved webhook dead-letter rows",
    "{event}",
  );
  voipEffectsReadyGauge.record(Math.max(0, health.effects.readyCount));
  voipEffectsOldestAgeGauge.record(
    Math.max(0, health.effects.oldestAgeSeconds),
  );
  voipEffectsFailedGauge.record(Math.max(0, health.effects.failedCount));
  voipDlqUnresolvedGauge.record(Math.max(0, health.dlq.unresolvedCount));
}

export function recordVoipMessageDispatchHealth(health: {
  pendingCount: number;
  ambiguousCount: number;
  oldestAgeSeconds: number;
}): void {
  voipMessagesPendingGauge = voipGauge(
    voipMessagesPendingGauge,
    "voip.messages.pending_count",
    "Messages awaiting provider acceptance",
    "{message}",
  );
  voipMessagesAmbiguousGauge = voipGauge(
    voipMessagesAmbiguousGauge,
    "voip.messages.ambiguous_count",
    "Messages with uncertain provider acceptance requiring reconciliation",
    "{message}",
  );
  voipMessagesOldestAgeGauge = voipGauge(
    voipMessagesOldestAgeGauge,
    "voip.messages.oldest_age_seconds",
    "Age of the oldest unsettled message",
    "s",
  );
  voipMessagesPendingGauge.record(Math.max(0, health.pendingCount));
  voipMessagesAmbiguousGauge.record(Math.max(0, health.ambiguousCount));
  voipMessagesOldestAgeGauge.record(Math.max(0, health.oldestAgeSeconds));
}

export function recordVoipWebhookAck(attrs: {
  durationMs: number;
  status: number;
}): void {
  const labels: Attributes = {
    status_class: `${Math.floor(attrs.status / 100)}xx`,
    status: attrs.status,
  };
  voipWebhookAckDuration ??= metrics
    .getMeter("@atriam/observability", "1.0.0")
    .createHistogram(ATTR_VOIP_WEBHOOK_ACK_DURATION, {
      description: "Telnyx webhook acknowledgement duration",
      unit: "ms",
    });
  voipWebhookAckRequests ??= metrics
    .getMeter("@atriam/observability", "1.0.0")
    .createCounter(ATTR_VOIP_WEBHOOK_ACK_REQUESTS, {
      description: "Telnyx webhook acknowledgement responses",
      unit: "{response}",
    });
  voipWebhookAckDuration.record(Math.max(0, attrs.durationMs), labels);
  voipWebhookAckRequests.add(1, labels);
}

export function recordVoipOperationDuration(
  operation: "inbox_claim" | "reducer_apply",
  durationMs: number,
  outcome: "ok" | "error" = "ok",
): void {
  const meter = metrics.getMeter("@atriam/observability", "1.0.0");
  if (operation === "inbox_claim") {
    voipInboxClaimDuration ??= meter.createHistogram(
      ATTR_VOIP_INBOX_CLAIM_DURATION,
      { description: "Webhook inbox claim transaction duration", unit: "ms" },
    );
    voipInboxClaimDuration.record(Math.max(0, durationMs), { outcome });
    return;
  }
  voipReducerApplyDuration ??= meter.createHistogram(
    ATTR_VOIP_REDUCER_APPLY_DURATION,
    { description: "Call reducer transaction duration", unit: "ms" },
  );
  voipReducerApplyDuration.record(Math.max(0, durationMs), { outcome });
}

export type VoipReliabilityEvent =
  | "version_conflict"
  | "lease_expired"
  | "lease_fence_rejected"
  | "effect_retry"
  | "effect_terminal_failure"
  | "dlq_inserted";

export function recordVoipReliabilityEvent(
  event: VoipReliabilityEvent,
  labels: Attributes = {},
): void {
  const meter = metrics.getMeter("@atriam/observability", "1.0.0");
  const counter = (() => {
    switch (event) {
      case "version_conflict":
        return (voipVersionConflicts ??= meter.createCounter(
          ATTR_VOIP_REDUCER_VERSION_CONFLICTS,
        ));
      case "lease_expired":
        return (voipLeaseExpired ??= meter.createCounter(
          ATTR_VOIP_LEASE_EXPIRED,
        ));
      case "lease_fence_rejected":
        return (voipLeaseFenceRejected ??= meter.createCounter(
          ATTR_VOIP_LEASE_FENCE_REJECTED,
        ));
      case "effect_retry":
        return (voipEffectRetries ??= meter.createCounter(
          ATTR_VOIP_EFFECTS_RETRIES,
        ));
      case "effect_terminal_failure":
        return (voipEffectTerminalFailures ??= meter.createCounter(
          ATTR_VOIP_EFFECTS_TERMINAL_FAILURES,
        ));
      case "dlq_inserted":
        return (voipDlqInserted ??= meter.createCounter(
          ATTR_VOIP_DLQ_INSERTED,
        ));
    }
  })();
  counter.add(1, labels);
}

export function recordVoipCommandReconciliation(
  outcome: "accepted" | "expired" | "error",
  provider: string,
): void {
  voipCommandReconciliation ??= metrics
    .getMeter("@atriam/observability", "1.0.0")
    .createCounter(ATTR_VOIP_COMMAND_RECONCILIATION, {
      description: "Durable VOIP command reconciliation outcomes",
    });
  voipCommandReconciliation.add(1, { outcome, provider });
}

/**
 * Drain the web metric reader. Schedule via next/server `after()`.
 *
 * Falls back to the global MeterProvider when the module-local reader is
 * unset: Next bundles this package separately into every route bundle, so the
 * copy that built the reader (instrumentation) and the copy a route helper
 * imports are different module instances. Counter increments reach the real
 * provider via the api global either way; the flush must too (same bug class
 * as flushServerLogs — see server-logger-provider.ts). No-op when neither
 * exists (tests, non-web runtimes — the api noop provider has no forceFlush).
 */
export async function flushServerMetrics(): Promise<void> {
  if (serverReader) {
    await serverReader.forceFlush();
    return;
  }
  const globalProvider = metrics.getMeterProvider() as {
    forceFlush?: () => Promise<void>;
  };
  if (typeof globalProvider?.forceFlush !== "function") {
    return;
  }
  await globalProvider.forceFlush();
}

/** Test-only: reset memoised state so each test exercises a fresh counter. */
export function __resetMetricsForTests(): void {
  serverReader = undefined;
  counter = undefined;
  requestCounter = undefined;
  durationHistogram = undefined;
  voipReadyGauge = undefined;
  voipOldestAgeGauge = undefined;
  voipEffectsReadyGauge = undefined;
  voipEffectsOldestAgeGauge = undefined;
  voipEffectsFailedGauge = undefined;
  voipDlqUnresolvedGauge = undefined;
  voipMessagesPendingGauge = undefined;
  voipMessagesAmbiguousGauge = undefined;
  voipMessagesOldestAgeGauge = undefined;
  voipWebhookAckDuration = undefined;
  voipWebhookAckRequests = undefined;
  voipInboxClaimDuration = undefined;
  voipReducerApplyDuration = undefined;
  voipVersionConflicts = undefined;
  voipLeaseExpired = undefined;
  voipLeaseFenceRejected = undefined;
  voipEffectRetries = undefined;
  voipEffectTerminalFailures = undefined;
  voipCommandReconciliation = undefined;
  voipDlqInserted = undefined;
}
